import React, { useEffect, useMemo, useState } from "react";
import { createCombat, updateCombat } from "../services/combatsServices";
import ScoreboardAdmin from "./../components/ScoreboardAdmin/ScoreboardAdmin";
import ScoreboardJJCombatAdmin from "./../components/ScoreboardJJCombat/ScoreboardAdminJJCombat";
import ScoreboardAdminNeWaza from "./../components/ScoreboardNeWaza/ScoreboardAdminNeWaza";
import DuoScoreboardAdmin from "./../components/ScoreboardAdmin/DuoScoreboardAdmin";
import {
  DISCIPLINES,
  buildAgeMappings,
  getSeasonStartYear,
} from "../utils/categoryRules";

const normalizeDisciplineKey = (value = "") => {
  const v = String(value).trim().toLowerCase();

  if (
    [
      "jujitsu duo",
      "ju-jitsu duo",
      "jj duo",
      "duo system",
      "jujitsu duo system",
      "ju-jitsu duo system",
    ].includes(v)
  ) {
    return "jj_duo";
  }

  if (
    [
      "jujitsu combat",
      "ju-jitsu combat",
      "jj combat",
      "jujitsu fighting",
      "ju-jitsu fighting",
      "ju-jitsu",
      "jujitsu",
    ].includes(v)
  ) {
    return "jj_combat";
  }

  if (
    [
      "ne-waza",
      "newaza",
      "jujitsu ne-waza",
      "ju-jitsu ne-waza",
      "jujitsu ne waza",
      "ju-jitsu ne waza",
      "bjj",
      "jiu-jitsu brésilien",
      "jiu jitsu brésilien",
      "jiu-jitsu bresilien",
      "jiu jitsu bresilien",
    ].includes(v)
  ) {
    return "ne_waza";
  }

  return "judo";
};

const extractBirthYear = (competiteur) => {
  const raw = String(
    competiteur?.ddn_competiteur ||
      competiteur?.ddn ||
      competiteur?.date_naissance ||
      competiteur?.birthdate ||
      "",
  ).trim();

  if (!raw) return null;

  const match = raw.match(/\b(19|20)\d{2}\b/);
  if (match) return Number(match[0]);

  const parsed = new Date(raw);
  const year = parsed.getFullYear();

  return Number.isFinite(year) ? year : null;
};

const getDisciplineForAgeRules = (disciplineKey) => {
  if (disciplineKey === "jj_combat" || disciplineKey === "jj_duo") {
    return DISCIPLINES.JUJITSU;
  }

  if (disciplineKey === "ne_waza") {
    return DISCIPLINES.NEWAZA;
  }

  return DISCIPLINES.JUDO;
};

const getCategorieAgeFromRules = ({
  competiteur1,
  competiteur2,
  disciplineKey,
  country = "France",
}) => {
  const birthYear =
    extractBirthYear(competiteur1) || extractBirthYear(competiteur2);

  if (!birthYear) return null;

  const disciplineForRules = getDisciplineForAgeRules(disciplineKey);
  const mappings = buildAgeMappings(getSeasonStartYear());
  const categories = mappings?.[disciplineForRules]?.[country] || [];

  const match = categories.find(({ range = [] }) => {
    const [minYear, maxYear] = range;
    return birthYear >= minYear && birthYear <= maxYear;
  });

  return match?.name || null;
};

const CombatModal = ({
  isOpen,
  onClose,
  competiteur1,
  competiteur2,
  onSubmit,
  initialData,
  distributionId,
  position1,
  position2,
  refreshPouleTable,
  competitionId,
  tapisId,
  discipline,
}) => {
  const [combatId, setCombatId] = useState(initialData?.id || null);
  const [combatResult, setCombatResult] = useState(null);

  const effectiveDiscipline =
    discipline ||
    competiteur1?.discipline_competiteur ||
    competiteur2?.discipline_competiteur ||
    "Judo";

  const disciplineKey = normalizeDisciplineKey(effectiveDiscipline);

  const categorieAge = useMemo(() => {
    return getCategorieAgeFromRules({
      competiteur1,
      competiteur2,
      disciplineKey,
      country: "France",
    });
  }, [competiteur1, competiteur2, disciplineKey]);

  const combatKey = `${competitionId}_${tapisId}`;

  useEffect(() => {
    if (isOpen) {
      setCombatId(initialData?.id || null);
      setCombatResult(null);
    }
  }, [isOpen, initialData]);

  useEffect(() => {
    const initCombat = async () => {
      if (
        !initialData &&
        competiteur1 &&
        competiteur2 &&
        competitionId &&
        distributionId
      ) {
        try {
          const payload = {
            competiteur_id1: competiteur1.id,
            competiteur_id2: competiteur2.id,
            distribution_id: distributionId,
            competition_id: competitionId,
          };

          const created = await createCombat(payload);
          setCombatId(created.id);
        } catch (error) {
          console.error("❌ Erreur création combat :", error);
        }
      }
    };

    if (isOpen) {
      setCombatResult(null);
      initCombat();
    }
  }, [
    isOpen,
    initialData,
    competiteur1,
    competiteur2,
    competitionId,
    distributionId,
  ]);

  const handleValidation = async () => {
    if (!combatResult) return;

    const payload = {
      competiteur_id1: competiteur1.id,
      competiteur_id2: competiteur2.id,
      distribution_id: distributionId,
      competition_id: competitionId,

      resultat_competiteur1: combatResult.result1,
      resultat_competiteur2: combatResult.result2,
      points_competiteur1: combatResult.points1,
      points_competiteur2: combatResult.points2,

      forfait_competiteur1: combatResult.forfait1 || false,
      forfait_competiteur2: combatResult.forfait2 || false,

      hansoku_competiteur1:
        combatResult.hansoku1 ||
        combatResult.disqualification1 ||
        false,
      hansoku_competiteur2:
        combatResult.hansoku2 ||
        combatResult.disqualification2 ||
        false,

      details_combat: combatResult.details_combat || null,

      // Champs complémentaires si ton back les accepte déjà
      avantages_competiteur1: combatResult.advantages1 ?? null,
      avantages_competiteur2: combatResult.advantages2 ?? null,
      penalites_competiteur1: combatResult.penalties1 ?? null,
      penalites_competiteur2: combatResult.penalties2 ?? null,
      submission_competiteur1: combatResult.submission1 || false,
      submission_competiteur2: combatResult.submission2 || false,
      medical_competiteur1: combatResult.medical1 || false,
      medical_competiteur2: combatResult.medical2 || false,
      golden_score: combatResult.golden_score || false,
    };

    try {
      if (combatId) {
        await updateCombat(combatId, payload);
      } else {
        const created = await createCombat(payload);
        setCombatId(created.id);
      }

      onSubmit(payload);
      refreshPouleTable();
      onClose();
    } catch (error) {
      console.error("❌ Erreur lors de l'enregistrement du combat :", error);
    }
  };

  if (!isOpen || !competiteur1 || !competiteur2) return null;

  const Board =
    disciplineKey === "jj_duo"
      ? DuoScoreboardAdmin
      : disciplineKey === "jj_combat"
        ? ScoreboardJJCombatAdmin
        : disciplineKey === "ne_waza"
          ? ScoreboardAdminNeWaza
          : ScoreboardAdmin;

  const boardDisciplineLabel =
    disciplineKey === "jj_combat"
      ? "Ju-Jitsu Combat"
      : disciplineKey === "jj_duo"
        ? "Jujitsu Duo"
        : disciplineKey === "ne_waza"
          ? "Ne-Waza"
          : "Judo";

  const Badge = ({ children, tone = "slate" }) => {
    const tones = {
      slate:
        "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 border-slate-300/60 dark:border-slate-700",
      blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 border-blue-300/60 dark:border-blue-700/60",
      violet:
        "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200 border-violet-300/60 dark:border-violet-700/60",
      emerald:
        "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200 border-emerald-300/60 dark:border-emerald-700/60",
      amber:
        "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 border-amber-300/60 dark:border-amber-700/60",
      rose: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200 border-rose-300/60 dark:border-rose-700/60",
    };

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold border ${
          tones[tone] || tones.slate
        }`}
      >
        {children}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex justify-center items-center">
      <div className="bg-gray-900 text-white dark:bg-gray-900 rounded-xl shadow-2xl w-full h-full flex flex-col overflow-hidden">
        <div className="sticky top-0 z-10 border-b border-gray-200/70 dark:border-gray-800/70 dark:bg-gray-900/90 backdrop-blur">
          <div className="px-3 py-2 sm:px-4 sm:py-3 text-center">
            <div className="text-base sm:text-lg md:text-xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">
              <span className="text-red-500">
                {position1}&nbsp;–&nbsp;
                {competiteur1.nom_competiteur?.toUpperCase()}{" "}
                {competiteur1.prenom_competiteur}
              </span>
              <span className="mx-2 text-gray-500 dark:text-gray-400">vs</span>
              <span className="text-gray-100 dark:text-white">
                {position2}&nbsp;–&nbsp;
                {competiteur2.nom_competiteur?.toUpperCase()}{" "}
                {competiteur2.prenom_competiteur}
              </span>
            </div>

            <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
              <Badge tone="violet">Distribution&nbsp;#{distributionId}</Badge>
              <Badge tone="emerald">
                Discipline&nbsp;{effectiveDiscipline}
              </Badge>
              {categorieAge && (
                <Badge tone="amber">Cat.&nbsp;{categorieAge}</Badge>
              )}
              {tapisId != null && (
                <Badge tone="rose">Tapis&nbsp;{tapisId}</Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0">
          <Board
            tapisId={tapisId}
            competiteur1={competiteur1}
            competiteur2={competiteur2}
            onValidate={(data) => setCombatResult(data)}
            competitionId={competitionId}
            categorieAge={categorieAge}
            combatKey={combatKey}
            discipline={boardDisciplineLabel}
          />
        </div>

        <div className="sticky bottom-0 z-10 border-t border-gray-200/70 dark:border-gray-800/70 bg-gray-900 dark:bg-gray-900/90 backdrop-blur">
          <div className="flex justify-center gap-3 sm:gap-6 p-3 sm:p-4 flex-wrap">
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white px-5 py-2 rounded-lg font-semibold shadow-sm"
            >
              Annuler
            </button>
            <button
              onClick={handleValidation}
              disabled={!combatResult}
              className={`px-5 py-2 rounded-lg font-semibold shadow-sm transition ${
                combatResult
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-green-200 text-white cursor-not-allowed"
              }`}
              title={
                !combatResult
                  ? "Saisir/valider le score dans le tableau"
                  : "Clôturer le combat"
              }
            >
              Clôturer le combat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CombatModal;